import { ValidationErrorCode, type ValidationError } from '@oysterjs/core/graphql';
import {
  VoiceAgentTransferDirectoryEntryType,
  type TransferDirectoryChangeInput
} from '~/api/graphql';
import { toE164 } from '~/lib/phone';
import { type CsvSchema } from '~/components/csv-import';
import { entryIdentity } from './voice-transfer-directory-csv';
import {
  ORIGINAL_ROW,
  type VoiceAgentSettingsTransferDirectoryEntryInput
} from './voice_agent_form_input';

type DirectoryRow = VoiceAgentSettingsTransferDirectoryEntryInput;

export const DIRECTORY_PATH = ['configuration', 'transfer', 'directory'];

// Server errors about a change are pathed [CHANGES_ARGUMENT, index], or
// [CHANGES_ARGUMENT, index, 'to', ...field] for a field of the row the change sends.
const CHANGES_ARGUMENT = 'transferDirectoryChanges';

// Must match canonicalDirectoryRow in voice_agent_transfer_directory.go, which the
// server uses to find the row a change names. Ignores differences that can't change
// routing: phone formatting, whitespace, list order, unset vs empty.

const canonical = (row: DirectoryRow): string => {
  const fields: [string, unknown][] = [];
  for (const [key, value] of Object.entries(row).sort(([a], [b]) => a.localeCompare(b))) {
    if (typeof value === 'string') {
      const trimmed = key === 'number' ? (toE164(value) ?? value.trim()) : value.trim();
      if (trimmed) {
        fields.push([key, trimmed]);
      }
    } else if (Array.isArray(value)) {
      if (value.length > 0) {
        fields.push([key, value.map((element) => JSON.stringify(element)).sort()]);
      }
    } else if (value !== null && value !== undefined) {
      fields.push([key, value]);
    }
  }
  return JSON.stringify(fields);
};

export const sameDirectoryRow = (a: DirectoryRow, b: DirectoryRow): boolean =>
  canonical(a) === canonical(b);

// JSON drops the symbol-keyed ORIGINAL_ROW, so only the row itself is sent.
const encode = (row: DirectoryRow) => JSON.stringify(row);

export type TransferDirectoryChanges = {
  changes: TransferDirectoryChangeInput[];
  /** For each change, the page row it came from; undefined for a deleted row. */
  rowOfChange: (number | undefined)[];
};

/**
 * Rows the editor changed, each paired with its loaded version via ORIGINAL_ROW so an
 * edit to any field is an update, not a delete plus add. The server applies a change to
 * its row as loaded. If someone else has changed that row since, it merges the edit into
 * the one row with the same Name, Type and Aliases, field by field; a field both changed
 * differently, a renamed row, or a delete is a conflict. `comparable` normalizes both
 * sides so filled-in defaults don't count as edits.
 */
export function diffTransferDirectory(
  loaded: DirectoryRow[],
  edited: DirectoryRow[],
  comparable: (row: DirectoryRow) => DirectoryRow = (row) => row
): TransferDirectoryChanges {
  // A list per key: legacy data can hold identical rows.
  const unclaimed = new Map<string, DirectoryRow[]>();
  for (const row of loaded) {
    const key = canonical(row);
    unclaimed.set(key, [...(unclaimed.get(key) ?? []), row]);
  }
  const claim = (row: DirectoryRow | undefined): DirectoryRow | undefined => {
    const copies = row && unclaimed.get(canonical(row));
    return copies?.shift();
  };

  const result: TransferDirectoryChanges = { changes: [], rowOfChange: [] };
  const record = (change: TransferDirectoryChangeInput, row: number | undefined) => {
    result.changes.push(change);
    result.rowOfChange.push(row);
  };

  edited.forEach((row, index) => {
    const loadedRow = claim(row[ORIGINAL_ROW]);
    if (!loadedRow) {
      record({ to: encode(comparable(row)) }, index);
    } else if (canonical(comparable(loadedRow)) !== canonical(comparable(row))) {
      record({ from: encode(loadedRow), to: encode(comparable(row)) }, index);
    }
  });
  for (const copies of unclaimed.values()) {
    for (const row of copies) {
      record({ from: encode(row) }, undefined);
    }
  }
  return result;
}

// Must match directoryConflictMessage in voice_agent_transfer_directory.go.
export const DIRECTORY_CONFLICT_MESSAGE =
  'Someone else changed the transfer directory while you were editing, so nothing was saved. Reload to see their changes, then make yours again.';

const DIRECTORY_CONFLICT: ValidationError = {
  code: ValidationErrorCode.Conflict,
  field: DIRECTORY_PATH,
  message: DIRECTORY_CONFLICT_MESSAGE
};

/** The directory row an error is on, or undefined for an error elsewhere. */
const directoryRowOfError = (error: ValidationError): number | undefined => {
  const isOnDirectoryRow =
    error.field.length > DIRECTORY_PATH.length &&
    DIRECTORY_PATH.every((segment, i) => error.field[i] === segment);
  return isOnDirectoryRow ? Number(error.field[DIRECTORY_PATH.length]) : undefined;
};

const isDirectoryConflict = (error: ValidationError): boolean =>
  error.code === ValidationErrorCode.Conflict &&
  error.field.length === DIRECTORY_PATH.length &&
  DIRECTORY_PATH.every((segment, i) => error.field[i] === segment);

/**
 * Maps change-indexed server errors onto page rows; a deleted row's error goes on the
 * directory. The server reports an error on a row the save changed or added against its
 * change, so a plain row error past the page's `pageRowCountAtSave` rows, or on one of
 * those rows, is about a row someone else saved. Those errors and every conflict on the
 * directory as a whole become one directory conflict, which asks for a reload.
 */
export function addressDirectoryErrorsByRow(
  errors: ValidationError[],
  rowOfChange: (number | undefined)[],
  pageRowCountAtSave: number
): ValidationError[] {
  const onPage = errors.map((error) => {
    const directoryRow = directoryRowOfError(error);
    const isOnSomeoneElsesRow =
      directoryRow !== undefined &&
      (directoryRow >= pageRowCountAtSave || rowOfChange.includes(directoryRow));
    if (isOnSomeoneElsesRow) {
      return DIRECTORY_CONFLICT;
    }
    if (error.field[0] !== CHANGES_ARGUMENT || error.field.length < 2) {
      return error;
    }
    const row = rowOfChange[Number(error.field[1])];
    return {
      ...error,
      field:
        row === undefined
          ? DIRECTORY_PATH
          : [...DIRECTORY_PATH, String(row), ...error.field.slice(3)]
    };
  });
  const withoutDirectoryConflicts = onPage.filter((error) => !isDirectoryConflict(error));
  return onPage.some(isDirectoryConflict)
    ? [...withoutDirectoryConflicts, DIRECTORY_CONFLICT]
    : onPage;
}

export const directoryRowHasError = (errors: ValidationError[], row: number): boolean => {
  const rowPath = [...DIRECTORY_PATH, String(row)];
  return errors.some((error) => rowPath.every((segment, i) => error.field[i] === segment));
};

/** A row with every field the mutation requires; old records return "" for enums. */
export const withSubmittedDefaults = (row: DirectoryRow): DirectoryRow => ({
  ...row,
  label: row.label ?? '',
  number: row.number ?? '',
  transferCriteria: row.transferCriteria ?? '',
  entryType: row.entryType || VoiceAgentTransferDirectoryEntryType.Internal,
  callTypes: row.callTypes ?? [],
  lineOfBusinessIds: row.lineOfBusinessIds ?? [],
  aliases: row.aliases ?? []
});

/**
 * Flags `rows` whose identity matches a different entry; exact copies collapse on save.
 * Only `rows` are checked so someone else's clash doesn't block untouched rows.
 */
export const directoryIdentityClashes = (
  schema: CsvSchema,
  directory: DirectoryRow[],
  rows: number[]
): ValidationError[] => {
  const identities = directory.map((row) => entryIdentity(schema, row));
  return rows
    .filter((row) =>
      directory.some(
        (other, index) =>
          index !== row &&
          identities[index] === identities[row] &&
          !sameDirectoryRow(other, directory[row])
      )
    )
    .map((row) => ({
      code: ValidationErrorCode.Invalid,
      field: [...DIRECTORY_PATH, String(row), 'aliases'],
      message:
        'Another entry has the same name, type and aliases. Give one of them different aliases to keep them apart.'
    }));
};

export const prepareDirectorySave = (
  schema: CsvSchema,
  saved: DirectoryRow[],
  submitted: DirectoryRow[],
  comparable?: (row: DirectoryRow) => DirectoryRow
) => {
  const { changes, rowOfChange } = diffTransferDirectory(saved, submitted, comparable);
  const clashes = directoryIdentityClashes(
    schema,
    submitted,
    rowOfChange.filter((row): row is number => row !== undefined)
  );
  return { changes, rowOfChange, clashes };
};
